import React, { useEffect, useMemo, useRef, useState } from "react";
import { map, reduce, reject, size, slice, take } from "lodash";
import { matchPath, useLocation } from "react-router-dom";
import styled from "styled-components";
import {
  Elevation,
  FontSize,
  FontWeight,
  Radius,
  Spacing,
  ZIndexes,
  background,
  borderRadius,
  content,
} from "@glossy";
import { dominant, onDominant } from "@glossy/theme";
import { tr } from "@linkfluence/i18n";
import { Locale } from "@linkfluence/models/locale";
import Icon from "@ui-kit/icon";
import Link from "@ui-kit/utils/link";
import { useFeature } from "@radarly/core/features/use-cases";
import { Page as PageId } from "@radarly/shared/router/pages";

const Root = styled.ul`
  display: flex;
  align-items: center;
  gap: ${Spacing.MediumLarge};
  color: ${content};
`;

const InnerSection = styled.div<{
  active: boolean;
  open: boolean;
  hasActivePage: boolean;
  hasSubPages: boolean;
}>`
  padding: ${Spacing.Medium};
  white-space: nowrap;
  position: relative;
  display: inline-block;
  color: ${(props) =>
    (props.active || props.open) && content(props, "secondary")};
  font-weight: ${(props) => props.active && FontWeight.Bold};
  cursor: pointer;
  :hover {
    color: ${(props) => content(props, "secondary")};
  }
  a {
    color: inherit;
  }
`;

const InlineLink = styled(Link)`
  display: inline-block;
  position: relative;
  line-height: 1;
`;

const Chevron = styled(Icon)`
  margin-left: ${Spacing.Small};
`;

const ActivePage = styled.div<{ open: boolean }>`
  display: inline-block;
  visibility: ${(props) => (props.open ? "hidden" : "visible")};
  color: ${dominant};
  font-weight: normal;
`;

const Dropdown = styled.ul<{ open: boolean }>`
  position: absolute;
  margin-top: 19px;
  margin-left: -16px;
  visibility: ${(props) => !props.open && "hidden"};
  background-color: ${onDominant};
  box-shadow: ${Elevation.Elevation_4};
  ${borderRadius("bottom", Radius.Small)};
  padding: ${Spacing.Medium} 0;
  z-index: ${ZIndexes.Top};

  ul {
    visibility: ${(props) => !props.open && "hidden !important"};
  }
`;

const More = styled.div<{ active?: boolean }>`
  padding: 0 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: normal;
  color: ${(props) => props.active && content(props, "secondary")};
  div {
    color: ${(props) => props.active && dominant};
  }
`;

const Subs = styled.li<{ hassubs?: boolean; active?: boolean }>`
  position: relative;
  line-height: 36px;
  color: ${content};
  &:hover {
    color: ${(props) => content(props, "secondary")};
    background-color: ${background};
  }

  > a {
    padding: 0 16px;
    height: 36px;
    display: block;
    font-weight: normal;
    color: ${(props) =>
      props.active ? content(props, "secondary") : "inerhit"};
  }

  ul {
    position: absolute;
    left: 100%;
    top: -10px;
    transition: ease-out 0.35s;
    background-color: ${onDominant};
    box-shadow: ${Elevation.Elevation_4};
    border-radius: ${Radius.Small};
    padding: ${Spacing.Medium} 0;
    margin-left: 1px;
  }

  &:not(:hover) ul {
    display: none;
  }
`;

const Beta = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  color: ${dominant};
  font-weight: initial;
  font-size: ${FontSize.Small};
`;

interface Page {
  id: string;
  label: string;
  disabled?: boolean;
  url?: string;
  beta?: boolean;
  pages?: Page[];
}

const matchPage = (page: Page, path: string) => matchPath(page.url || "", path);

const getActiveSection = (pages: Page[], path: string) =>
  reduce(
    pages,
    (acc, page) => {
      if (matchPage(page, path)) {
        acc = page.id;
      } else if (size(page.pages)) {
        return reduce(
          page.pages,
          (acc2, subpage) => {
            return matchPage(subpage, path) ? page.id : acc2;
          },
          acc
        );
      }
      return acc;
    },
    ""
  );

const getActivePage = (pages: Page[], path: string) =>
  reduce(
    pages,
    (acc, page) => {
      if (size(page.pages)) {
        return reduce(
          page.pages,
          (acc2, subpage) => {
            return matchPage(subpage, path) ? subpage : acc2;
          },
          acc
        );
      }
      return acc;
    },
    null
  );

const getEnabledPages = (pages: Page[]): Page[] =>
  map(pages, (page) => ({
    ...page,
    pages: reject(page.pages, { disabled: true }),
  }));

const isBeta = (page, discoveryGA): boolean =>
  !discoveryGA && [PageId.discovery].includes(page.id);

interface IProps {
  pages: Page[];
  more?: number;
  locale: Locale;
}

const KomonNavigationNavbarPages = ({ pages, more, locale }: IProps) => {
  const location = useLocation();
  const path = location ? location.pathname : "";
  const [openId, setOpenId] = useState("");
  const section = getActiveSection(pages, path);
  const enabledPages = useMemo(() => getEnabledPages(pages), [pages]);
  const activePage = getActivePage(enabledPages, path);
  const ref = useRef<HTMLUListElement>(null);
  const discoveryGA = useFeature("discovery_global_availability");

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (openId !== "") {
        if (e.target.nodeName === "A") {
          setOpenId("");
        } else {
          if (!ref.current.contains(e.target)) {
            setOpenId("");
          }
        }
      }
    };
    window.addEventListener("click", handleClickOutside);
    return () => {
      window.removeEventListener("click", handleClickOutside);
    };
  });

  return (
    <Root ref={ref}>
      {map(enabledPages, (page, idx) => {
        const isOpen = openId === page.id;
        const isActive = page.id === section;
        const hasActivePage = isActive && activePage;
        const morePages = more ? slice(page.pages, more) : [];
        const hasUrl = page.url && !size(page.pages);
        const beta = isBeta(page, discoveryGA);
        return (
          <div key={idx}>
            <InnerSection
              onClick={() => !hasUrl && setOpenId(isOpen ? "" : page.id)}
              open={isOpen}
              active={isActive}
              hasActivePage={hasActivePage}
              hasSubPages={!hasUrl}
            >
              <InlineLink url={hasUrl ? page.url : ""}>
                {page.label}
                {beta && <Beta>Beta</Beta>}
              </InlineLink>
              {!!size(page.pages) && (
                <>
                  <Chevron
                    glyph={
                      hasActivePage && !isOpen
                        ? "chevron-right"
                        : "chevron-down"
                    }
                    size={10}
                  />
                  <Dropdown open={isOpen}>
                    {map(more ? take(page.pages, more) : page.pages, (page) => (
                      <Subs
                        active={activePage && activePage.id === page.id}
                        key={page.id}
                      >
                        <Link url={page.url}>{page.label}</Link>
                      </Subs>
                    ))}
                    {!!size(morePages) && (
                      <>
                        <Subs hassubs>
                          <More
                            active={getActiveSection(morePages, path) !== ""}
                          >
                            {tr(locale, "HEADER::MORE")}
                            <Icon glyph="chevron-right" size={10} />
                          </More>

                          <ul>
                            {map(morePages, (morePage) => (
                              <Subs
                                active={
                                  activePage && activePage.id === morePage.id
                                }
                                key={morePage.id}
                              >
                                <Link url={morePage.url}>{morePage.label}</Link>
                              </Subs>
                            ))}
                          </ul>
                        </Subs>
                      </>
                    )}
                  </Dropdown>
                </>
              )}
            </InnerSection>
            {hasActivePage && (
              <ActivePage open={isOpen}>{activePage.label}</ActivePage>
            )}
          </div>
        );
      })}
    </Root>
  );
};

export default KomonNavigationNavbarPages;
